import { useState, useEffect } from "react";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ResponsiveDialog } from "@/components/ui/ResponsiveDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { track, EVENTS } from "@/lib/analytics";
import {
  ShieldCheck,
  KeyRound,
  Crown,
  Sparkles,
  Loader2,
  ArrowRight,
  Check,
  Copy,
  Clock,
  FileUp,
  FileCheck2,
} from "lucide-react";

import {
  fetchTiers,
  submitClaim,
  verifyClaim,
  createCheckout,
  getClaimStatus,
  uploadClaimProof,
} from "@/lib/sponsor";

const STEPS = ["claim", "verify", "proof", "review", "tier"];

export default function ClaimSponsorDialog({
  open,
  onOpenChange,
  restaurant,
}) {
  const [step, setStep] = useState("claim");
  const [tiers, setTiers] = useState([]);
  const [claim, setClaim] = useState(null);
  const [devCode, setDevCode] = useState("");
  const [loading, setLoading] = useState(false);

  // form fields (halal status + proof move out of step 1 for a simpler intro)
  const [form, setForm] = useState({
    owner_name: "",
    email: "",
    phone: "",
    role: "Owner",
  });
  const [code, setCode] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [proofUploaded, setProofUploaded] = useState(null);

  useEffect(() => {
    if (open) {
      setStep("claim");
      setClaim(null);
      setDevCode("");
      setCode("");
      setProofFile(null);
      setProofUploaded(null);
      setForm({
        owner_name: "",
        email: "",
        phone: "",
        role: "Owner",
      });
    }
  }, [open, restaurant?.place_id]);

  useEffect(() => {
    if (open && tiers.length === 0) {
      fetchTiers().then(setTiers).catch(console.error);
    }
  }, [open, tiers.length]);

  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    if (!restaurant) return;
    setLoading(true);
    try {
      const payload = {
        place_id: restaurant.place_id,
        restaurant_name: restaurant.name,
        owner_name: form.owner_name,
        email: form.email,
        phone: form.phone,
        role: form.role,
      };
      const res = await submitClaim(payload);
      setClaim(res);
      setDevCode(res.dev_code);
      // Owner has filled in name/email/phone — biggest signal of intent.
      track(EVENTS.CLAIM_SUBMITTED, {
        place_id: restaurant.place_id,
        restaurant_name: restaurant.name,
      });
      setStep("verify");
    } catch (err) {
      toast.error("Couldn't submit claim", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!claim) return;
    setLoading(true);
    try {
      const res = await verifyClaim(claim.claim_id, code);
      // Flow: OTP -> proof upload -> admin review -> (on approval) tier picker.
      // If the claim is somehow already verified, skip ahead to tier.
      if (res.verified || res.status === "verified") {
        toast.success("Ownership already verified");
        track(EVENTS.CLAIM_VERIFIED, { place_id: restaurant.place_id });
        setStep("tier");
      } else {
        toast.success("Code accepted — upload ownership proof next");
        setStep("proof");
      }
    } catch (err) {
      toast.error("Verification failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProofUpload = async (e) => {
    e.preventDefault();
    if (!claim || !proofFile) return;
    setLoading(true);
    try {
      const res = await uploadClaimProof(claim.claim_id, proofFile);
      setProofUploaded(res);
      toast.success("Proof uploaded — your claim is now in admin review");
      setStep("review");
    } catch (err) {
      // axios's err.message is usually "Network Error" for any failed
      // request — useless to the user. Surface the real cause: server
      // detail when we got a response, request-config diagnostics when
      // the request never reached the server.
      const serverDetail = err?.response?.data?.detail;
      let description = serverDetail;
      if (!description) {
        const status = err?.response?.status;
        const code = err?.code;
        if (status) {
          description = `Server responded ${status}. Try a smaller file or a different format.`;
        } else if (code === "ECONNABORTED") {
          description = "Upload timed out — please check your connection and try again.";
        } else {
          // No response at all (network-layer error). Include axios's code
          // so we can debug WAF/CORS/TLS issues at the edge.
          description = `Upload didn't reach the server${code ? ` (${code})` : ""}. If this keeps happening on Wi-Fi, try mobile data once.`;
        }
      }
      toast.error("Upload failed", { description });
    } finally {
      setLoading(false);
    }
  };

  const handleSkipProof = () => {
    toast.message("Skipped proof — admin may ask for it before approving", {
      description: "You can return to upload later.",
    });
    setStep("review");
  };

  // Poll claim status while we're on the review step in case the admin
  // approves while the dialog is open.
  useEffect(() => {
    if (step !== "review" || !claim?.claim_id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await getClaimStatus(claim.claim_id);
        if (cancelled) return;
        if (res.verified) {
          toast.success("Admin approved your claim — pick a plan!");
          setStep("tier");
        }
      } catch (err) {
        // Polling is best-effort — don't spam toasts on transient errors,
        // but log so genuine outages are debuggable.
        console.warn("[claim-poll] status check failed:", err?.message || err);
      }
    };
    const id = setInterval(tick, 8000);
    tick();
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [step, claim?.claim_id]);

  const handleTierSelect = async (tier_id) => {
    if (!claim) return;
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await createCheckout(claim.claim_id, tier_id, origin);
      // Persist for the /sponsor/success page
      try {
        sessionStorage.setItem(
          "halal_sponsor_session",
          JSON.stringify({
            session_id: res.session_id,
            tier_id,
            restaurant_name: restaurant?.name,
          })
        );
      } catch (storageErr) {
        // sessionStorage may be unavailable (private browsing, locked-down
        // webviews). The success page degrades gracefully when this is empty.
        console.warn("[sponsor] sessionStorage write failed:", storageErr?.message);
      }
      track(EVENTS.SPONSOR_CHECKOUT_STARTED, {
        place_id: restaurant?.place_id,
        tier_id,
      });
      window.location.href = res.url;
    } catch (err) {
      toast.error("Checkout failed", {
        description: err?.response?.data?.detail || err.message,
      });
      setLoading(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      testId="claim-sponsor-dialog"
    >
      <div className="flex flex-col">
        {/* Header strip */}
        <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-[#FDFBF7] to-[#F2EDE3] border-b border-[#E5E1D8] shrink-0">
          <DialogHeader className="space-y-1.5 text-left">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#D95D39] flex items-center justify-center">
                <Crown className="w-4 h-4 text-white" />
              </div>
              <DialogTitle className="font-display text-xl font-medium text-[#2A3B2C]">
                Boost {restaurant?.name || "your restaurant"}
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-[#647466]">
              Verify ownership, then choose a featured-listing plan.
            </DialogDescription>
          </DialogHeader>
          <StepIndicator current={step} />
        </div>

        <div
          className="
            p-6 overflow-y-auto brand-scroll
            max-h-[calc(85dvh-130px)]
            sm:max-h-[60vh]
          "
          style={{ WebkitOverflowScrolling: "touch" }}
          onFocus={(e) => {
            const t = e.target;
            if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) {
              setTimeout(() => t.scrollIntoView({ block: "center", behavior: "smooth" }), 250);
            }
          }}
        >
          <AnimatePresence mode="wait">
            {step === "claim" && (
              <motion.form
                key="claim"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleClaimSubmit}
                className="space-y-4"
                data-testid="claim-form"
              >
                <Field
                  label="Your full name"
                  required
                  value={form.owner_name}
                  onChange={(v) => setForm({ ...form, owner_name: v })}
                  placeholder="Al Koncierge"
                  testId="claim-name"
                />
                <Field
                  label="Business email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                  placeholder="hello@alkoncierge.com"
                  testId="claim-email"
                />
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Phone"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    placeholder="+971 50 ..."
                    testId="claim-phone"
                  />
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
                      Your role
                    </Label>
                    <select
                      data-testid="claim-role"
                      value={form.role}
                      onChange={(e) =>
                        setForm({ ...form, role: e.target.value })
                      }
                      className="w-full h-10 rounded-xl border border-[#E5E1D8] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D95D39]"
                    >
                      <option>Owner</option>
                      <option>Manager</option>
                      <option>Marketing Lead</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
                <div className="rounded-2xl border border-[#E5E1D8] bg-[#FDFBF7] px-3.5 py-3 text-[11px] text-[#647466] leading-relaxed">
                  We&apos;ll email you a 6-digit code, then ask for an ownership
                  document (business licence, utility bill, or halal cert).
                  Halal-status details and certifier info live on your owner
                  dashboard so you can update them any time.
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="claim-submit"
                  className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] disabled:opacity-60 text-white font-medium transition-colors"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Send verification code
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </motion.form>
            )}

            {step === "verify" && (
              <motion.form
                key="verify"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleVerify}
                className="space-y-5"
                data-testid="verify-form"
              >
                <div className="text-center">
                  <div className="mx-auto w-14 h-14 rounded-full bg-[#FFF3EE] flex items-center justify-center">
                    <KeyRound className="w-6 h-6 text-[#D95D39]" />
                  </div>
                  <p className="mt-3 text-sm text-[#647466]">
                    We've sent a 6-digit code to{" "}
                    <span className="font-semibold text-[#2A3B2C]">
                      {form.email}
                    </span>
                  </p>
                </div>

                {devCode ? (
                  <DevCodeBanner
                    code={devCode}
                    onCopy={() => setCode(devCode)}
                  />
                ) : (
                  <div
                    data-testid="claim-code-sent-banner"
                    className="rounded-xl border border-[#2E7D5B]/40 bg-[#F0F8F4] px-3 py-2 text-xs text-[#1F5C42] flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2E7D5B]" />
                    We just emailed your 6-digit code to {form.email}.
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
                    Verification code
                  </Label>
                  <Input
                    data-testid="verify-code-input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="123456"
                    className="text-center text-2xl tracking-[0.5em] font-display h-14 border-[#E5E1D8] focus-visible:ring-[#D95D39] rounded-xl"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  data-testid="verify-submit"
                  className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] disabled:opacity-50 text-white font-medium transition-colors"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4" />
                      Verify ownership
                    </>
                  )}
                </button>
              </motion.form>
            )}

            {step === "proof" && (
              <motion.form
                key="proof"
                onSubmit={handleProofUpload}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
                data-testid="proof-step"
              >
                <div className="text-center">
                  <div className="mx-auto w-12 h-12 rounded-full bg-[#FFF3EE] flex items-center justify-center">
                    <FileUp className="w-5 h-5 text-[#D95D39]" />
                  </div>
                  <h4 className="mt-2 font-display text-lg font-medium text-[#2A3B2C]">
                    Upload ownership proof
                  </h4>
                  <p className="mt-1 text-xs text-[#647466]">
                    A document, photo, or short video that ties you to the
                    business — business registration, utility bill, halal
                    certificate, a recent invoice, or a clip of you in the
                    venue.
                  </p>
                  <div className="mt-3 rounded-xl border border-[#D95D39]/30 bg-[#FFF3EE] px-3 py-2 text-[11px] text-[#9C3A1C] font-medium leading-relaxed">
                    Ownership is only accepted when proof is{" "}
                    <span className="font-bold">beyond reasonable doubt</span>.
                    Stronger proof (multiple sources, video walkthrough, or an
                    official halal cert in your name) gets approved faster.
                  </div>
                </div>

                <label
                  htmlFor="proof-file-input"
                  className={`block rounded-2xl border-2 border-dashed p-5 text-center cursor-pointer transition-colors ${
                    proofFile
                      ? "border-[#2A3B2C] bg-[#2A3B2C]/5"
                      : "border-[#E5E1D8] hover:border-[#D95D39] hover:bg-[#FFF3EE]/40"
                  }`}
                >
                  <input
                    id="proof-file-input"
                    data-testid="proof-file-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,video/mp4,video/quicktime,video/webm,.mov,.mp4,.webm,.pdf,.heic,.heif"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      // Videos get a 50 MB cap; docs/photos 8 MB.
                      const isVideo =
                        (f.type || "").startsWith("video/") ||
                        /\.(mp4|mov|webm)$/i.test(f.name);
                      const cap = isVideo
                        ? 50 * 1024 * 1024
                        : 8 * 1024 * 1024;
                      if (f.size > cap) {
                        toast.error(
                          `File exceeds ${isVideo ? "50 MB" : "8 MB"} limit`
                        );
                        return;
                      }
                      setProofFile(f);
                    }}
                  />
                  {proofFile ? (
                    <div className="flex items-center justify-center gap-2 text-sm">
                      <FileCheck2 className="w-4 h-4 text-[#2A3B2C]" />
                      <span className="font-medium text-[#2A3B2C] truncate max-w-[240px]">
                        {proofFile.name}
                      </span>
                      <span className="text-xs text-[#647466]">
                        {(proofFile.size / 1024).toFixed(0)} KB
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <FileUp className="w-6 h-6 mx-auto text-[#647466]" />
                      <div className="text-sm font-medium text-[#2A3B2C]">
                        Click to choose a file
                      </div>
                      <div className="text-[11px] text-[#8A9A8B]">
                        Document, photo, or video · 8 MB (docs/photos) · 50 MB (video)
                      </div>
                    </div>
                  )}
                </label>

                <div className="rounded-xl bg-[#FDFBF7] border border-[#E5E1D8] p-3 text-[11px] text-[#647466] leading-relaxed">
                  Your document is encrypted in transit, stored privately, and
                  only viewable by AlKoncierge admins reviewing your claim. We
                  fingerprint each upload (SHA-256) to detect duplicates.
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="proof-skip-btn"
                    onClick={handleSkipProof}
                    className="h-11 rounded-full bg-white border border-[#E5E1D8] text-[#647466] text-sm font-medium hover:border-[#D95D39] hover:text-[#D95D39]"
                  >
                    Skip for now
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !proofFile}
                    data-testid="proof-upload-btn"
                    className="h-11 rounded-full bg-[#2A3B2C] hover:bg-[#1f2b21] disabled:opacity-40 text-white text-sm font-medium inline-flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Upload
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </motion.form>
            )}

            {step === "review" && (
              <motion.div
                key="review"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="text-center py-3 space-y-4"
                data-testid="review-step"
              >
                <div className="mx-auto w-14 h-14 rounded-full bg-[#FFF3EE] flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#D95D39]" />
                </div>
                <div>
                  <h4 className="font-display text-lg font-medium text-[#2A3B2C]">
                    Awaiting admin approval
                  </h4>
                  <p className="mt-1 text-sm text-[#647466]">
                    Code accepted. An admin will review your claim shortly
                    (usually within a few hours). We'll email{" "}
                    <span className="font-semibold text-[#2A3B2C]">
                      {form.email}
                    </span>{" "}
                    when you're approved.
                  </p>
                </div>
                <div className="rounded-2xl bg-[#FDFBF7] border border-[#E5E1D8] p-3 text-left text-xs text-[#647466]">
                  <div className="font-bold uppercase tracking-wider text-[10px] text-[#8A9A8B] mb-1">
                    What happens next
                  </div>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Admin verifies the halal/business info you provided</li>
                    {proofUploaded ? (
                      <li>
                        Your proof document (
                        <code className="font-mono">
                          {proofUploaded.sha256?.slice(0, 8)}…
                        </code>
                        ) is queued for review
                      </li>
                    ) : (
                      <li>
                        You haven't uploaded proof yet — admin may request it
                        before approving
                      </li>
                    )}
                    <li>Once approved you can choose a featured-listing plan</li>
                    <li>Sign in at <code>/owner</code> any time to check status</li>
                  </ul>
                </div>
                <button
                  type="button"
                  data-testid="review-close"
                  onClick={() => onOpenChange(false)}
                  className="w-full h-11 rounded-full bg-[#2A3B2C] hover:bg-[#1f2b21] text-white text-sm font-medium"
                >
                  Got it
                </button>
              </motion.div>
            )}

            {step === "tier" && (
              <motion.div
                key="tier"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-3"
                data-testid="tier-list"
              >
                <p className="text-sm text-[#647466]">
                  Choose how long you'd like to be featured. One-time payment,
                  no auto-renewal.
                </p>
                {tiers.map((t) => (
                  <TierCard
                    key={t.id}
                    tier={t}
                    onSelect={() => handleTierSelect(t.id)}
                    disabled={loading}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </ResponsiveDialog>
  );
}

// ----- sub components -----

function StepIndicator({ current }) {
  const idx = STEPS.indexOf(current);
  return (
    <div className="mt-4 flex items-center gap-2" data-testid="step-indicator">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center flex-1">
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
              i <= idx
                ? "bg-[#D95D39] text-white"
                : "bg-white border border-[#E5E1D8] text-[#8A9A8B]"
            }`}
          >
            {i < idx ? <Check className="w-3 h-3" /> : i + 1}
          </div>
          {i < STEPS.length - 1 && (
            <div
              className={`flex-1 h-px mx-1 transition-colors ${
                i < idx ? "bg-[#D95D39]" : "bg-[#E5E1D8]"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", required, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
        {label}
        {required && <span className="text-[#D95D39] ml-1">*</span>}
      </Label>
      <Input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="border-[#E5E1D8] focus-visible:ring-[#D95D39] rounded-xl h-10"
      />
    </div>
  );
}

function DevCodeBanner({ code, onCopy }) {
  return (
    <div
      data-testid="dev-code-banner"
      className="rounded-xl border border-dashed border-[#E1A624] bg-[#FFFBF0] px-3 py-2.5 flex items-center justify-between gap-3"
    >
      <div className="text-xs text-[#647466] leading-snug">
        <span className="font-bold text-[#E1A624]">DEMO MODE</span> — no email
        provider yet. Your code:{" "}
        <span className="font-mono font-bold text-[#2A3B2C]">{code}</span>
      </div>
      <button
        type="button"
        onClick={onCopy}
        data-testid="dev-code-copy"
        className="inline-flex items-center gap-1 text-xs font-medium text-[#D95D39] hover:text-[#C54E2C]"
      >
        <Copy className="w-3 h-3" />
        Use
      </button>
    </div>
  );
}

function TierCard({ tier, onSelect, disabled }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      data-testid={`tier-card-${tier.id}`}
      className={`relative w-full text-left rounded-2xl border p-4 transition-all duration-200 disabled:opacity-60 hover:-translate-y-0.5 hover:shadow-md ${
        tier.popular
          ? "border-[#D95D39] bg-gradient-to-br from-white to-[#FFF3EE] shadow-sm"
          : "border-[#E5E1D8] bg-white"
      }`}
    >
      {tier.popular && (
        <span className="absolute -top-2.5 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#D95D39] text-white">
          Most Popular
        </span>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles
              className={`w-4 h-4 ${
                tier.id === "premium"
                  ? "text-[#E1A624]"
                  : tier.id === "pro"
                  ? "text-[#D95D39]"
                  : "text-[#8A9A8B]"
              }`}
            />
            <h4 className="font-display text-lg font-medium text-[#2A3B2C]">
              {tier.name}
            </h4>
          </div>
          <p className="text-xs text-[#647466] mt-0.5">{tier.tagline}</p>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-semibold text-[#2A3B2C]">
            ${Math.round(tier.amount).toLocaleString()}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[#8A9A8B]">
            one-time
          </div>
        </div>
      </div>
      <ul className="mt-3 space-y-1">
        {tier.perks.map((p) => (
          <li
            key={p}
            className="flex items-start gap-1.5 text-xs text-[#647466]"
          >
            <Check className="w-3.5 h-3.5 mt-0.5 text-[#D95D39] shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </button>
  );
}
